<template>
    <el-dropdown
        ref="dropdownRef"
        popper-class="custom-dropdown-new dropdown-megamenu"
        :show-arrow="false"
        @visible-change="(v: boolean) => { dropdownOpen = v; $emit('visibleChange', v) }"
    >
        <span class="text-16 inline-flex items-center gap-4px">
            {{ t('common.projects') }}
            <icon-svg-arrow-down class="w-12 h-12 transition-transform duration-200" :class="{ 'rotate-180': dropdownOpen }" />
        </span>
        <template #dropdown>
            <div class="mm-panel">
                <!-- 顶部横幅：获取 FlagOS（原型 .download-strip） -->
                <div class="mm-promo cursor-pointer" @click="handlePromoClick">
                    <span class="mm-promo-icon">
                        <svg
                            width="21"
                            height="21"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M12 3v12" />
                            <path d="m16 11-4 4-4-4" />
                            <path d="M5 20h14" />
                        </svg>
                    </span>
                    <div class="mm-promo-text">
                        <p class="mm-promo-title">{{ t('tools.menuPromo.title') }}</p>
                        <p class="mm-promo-sub">{{ t('tools.menuPromo.subtitle') }}</p>
                    </div>
                    <div class="mm-promo-btn">
                        <span>{{ t('tools.menuPromo.download') }}</span>
                        <icon-svg-arrow-right />
                    </div>
                </div>

                <!-- 三栏：开源核心库 / 生态使能项目 / 领域专项（原型 .stack-columns） -->
                <div class="mm-body">
                    <div v-for="(group, gIdx) in dropdownList" :key="gIdx" class="mm-col">
                        <p class="mm-col-title">{{ label(group.name) }}</p>

                        <!-- 左栏「开源核心库」：算子库分组 + 基础组件卡 -->
                        <template v-if="group.layout === 'cards'">
                            <!-- 算子库分组：通用 / 融合 / 多领域（原型 .operator-groups） -->
                            <div class="mm-operator-groups">
                                <div v-for="(card, cIdx) in subgroupCards(group)" :key="cIdx" class="mm-subgroup" :class="{ 'is-wide': card.wide }">
                                    <p class="mm-subgroup-title">{{ label(card.name) }}</p>
                                    <div class="mm-link-grid" :class="{ 'is-grid': card.columns === 2 }">
                                        <div v-for="(item, iIdx) in itemsOf(card)" :key="iIdx" class="mm-mini-link" @click="handleEntryClick(item)">
                                            <span class="mm-mini-main">
                                                <b>
                                                    {{ label(item.name) }}
                                                    <icon-svg-new-tag v-if="item.badge" class="mm-badge" :aria-label="item.badge" />
                                                </b>
                                                <small v-if="item.desc">{{ label(item.desc) }}</small>
                                            </span>
                                            <span class="mm-mini-arrow">→</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 基础组件卡：编译器 / 训练与推理框架 / 通信库（原型 .core-foundations） -->
                            <div class="mm-foundations">
                                <div v-for="(card, cIdx) in foundationCards(group)" :key="cIdx" class="mm-foundation-card">
                                    <p class="mm-foundation-title">{{ label(card.name) }}</p>
                                    <span
                                        v-for="(item, iIdx) in itemsOf(card)"
                                        :key="iIdx"
                                        class="mm-foundation-link"
                                        @click="handleEntryClick(item)"
                                    >
                                        {{ label(item.name) }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <!-- 中/右栏：普通条目列表（原型 .menu-list / .menu-card） -->
                        <div v-else class="mm-list">
                            <div v-for="(item, iIdx) in itemsOf(group)" :key="iIdx" class="mm-card" @click="handleEntryClick(item)">
                                <b class="mm-card-name">
                                    {{ label(item.name) }}
                                    <icon-svg-new-tag v-if="item.badge" class="mm-badge" :aria-label="item.badge" />
                                </b>
                                <span v-if="item.desc" class="mm-card-desc">{{ label(item.desc) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </el-dropdown>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { menuList, type DropdownItem } from '@/layout/menuData';

const emit = defineEmits<{
    (e: 'visibleChange', visible: boolean): void;
    (e: 'jumpPage', child: DropdownItem): void;
}>();

const { t } = useI18n();

const dropdownOpen = ref(false);

const dropdownList = menuList.find(m => m.name === 'common.projects')!.dropdownList!;

/**
 * 安全取文案：key 为空/undefined 时返回空串
 * 分类节点没有 desc，直接 t(undefined) 会抛错并导致整页渲染失败
 */
const label = (key?: string) => (key ? t(key) : '');

/** 统一成「条目数组」：分类取其子项，普通条目即自身 */
const itemsOf = (item: DropdownItem): DropdownItem[] => item.list || [];

/** 左栏算子库分组卡（通用 / 融合 / 多领域） */
const subgroupCards = (group: DropdownItem) => itemsOf(group).filter(card => card.variant !== 'foundation');

/** 左栏基础组件卡（编译器 / 训练与推理框架 / 通信库） */
const foundationCards = (group: DropdownItem) => itemsOf(group).filter(card => card.variant === 'foundation');

const dropdownRef = ref();

/** 点击条目：先收起弹层再触发跳转（面板为自定义结构，不会自动关闭） */
const handleEntryClick = (item: DropdownItem) => {
    emit('jumpPage', item);
    dropdownRef.value?.handleClose?.();
};

/** 顶部横幅的「资源下载」入口 */
const handlePromoClick = () => {
    handleEntryClick({ name: 'common.resourcedownload', path: '/resourcedownload', type: 'route' });
};
</script>

<style lang="scss">
/*
 * 导航下拉（软件栈 / 工具&平台 / 开发者中心）共享的弹层基元：样式取自设计原型「FlagOS 2.2 官网导航及首页优化」
 * 对应关系：
 *   .mm-panel             ← .mega-menu
 *   .mm-promo             ← .download-strip / .download-icon / .download-copy / .download-action
 *   .mm-body              ← .mega-body + .stack-columns
 *   .mm-body.is-simple    ← .mega-body.simple-columns（工具&平台，无横幅，上内边距 22px）
 *   .mm-body.is-doc       ← .mega-body.simple-columns.developer-columns（开发者中心，与前者同几何）
 *   .mm-col-title         ← .mega-title-row > h3
 *   .mm-tile-grid         ← .tile-grid
 *   .mm-tile              ← .menu-tile
 *   .mm-operator-groups   ← .operator-groups
 *   .mm-subgroup          ← .subgroup （算子库分组卡）
 *   .mm-mini-link         ← .mini-link.described
 *   .mm-foundations       ← .core-foundations
 *   .mm-foundation-card   ← .foundation-card
 *   .mm-list / .mm-card   ← .menu-list / .menu-card
 */
.dropdown-megamenu {
    /* 原型 :root 设计变量 */
    --mm-brand: #3846fa;
    --mm-brand-cyan: #329cff;
    --mm-brand-purple: #3d2cff;
    --mm-ink: #262642;
    --mm-muted: #647787;
    --mm-muted-2: #8b96aa;
    --mm-line: #e7e9ef;
    --mm-soft-blue: #f3f6ff;

    background: rgba(255, 255, 255, 0.99) !important;
    padding: 0 !important;
    /* 原型 .mega-menu：整幅贴边展开，无描边、无圆角，只留投影 */
    border: 0 !important;
    border-radius: 0 !important;

    .el-scrollbar__view,
    .el-dropdown__list {
        padding: 0 !important;
    }

    /* 收起 el-dropdown 自身滚动容器，由 .mm-panel 统一滚动 */
    .el-scrollbar,
    .el-scrollbar__wrap {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
    }

    /* 原型 .mega-menu：等高视口、超高时内部滚动 */
    .mm-panel {
        max-height: calc(100vh - 94px);
        overflow-x: hidden;
        overflow-y: auto;
        scrollbar-width: thin;
    }

    /* ---------- 顶部横幅：获取 FlagOS（原型 .download-strip） ---------- */
    .mm-promo {
        position: sticky;
        top: 0;
        z-index: 5;
        display: flex;
        align-items: center;
        gap: 18px;
        min-height: 66px;
        /* 与下方三栏同宽居中，同时保证背景横向铺满 */
        padding: 12px max(30px, calc((100vw - 1420px) / 2 + 30px));
        color: #263259;
        background: linear-gradient(90deg, #f3f5ff 0%, #edf7ff 100%);
        border-bottom: 1px solid rgba(56, 70, 250, 0.12);
    }

    .mm-promo-icon {
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        width: 40px;
        height: 40px;
        color: #fff;
        border-radius: 12px;
        background: linear-gradient(135deg, var(--mm-brand-purple), var(--mm-brand-cyan));
        box-shadow: 0 10px 22px rgba(56, 70, 250, 0.2);
    }

    .mm-promo-text {
        flex: 1 1 auto;
        min-width: 0;
    }

    .mm-promo-title {
        margin-bottom: 3px;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.4;
    }

    .mm-promo-sub {
        font-size: 13px;
        line-height: 1.4;
        color: var(--mm-muted);
    }

    .mm-promo-btn {
        flex: 0 0 auto;
        margin-left: auto;
        min-width: 132px;
        height: 40px;
        padding: 0 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #fff;
        border-radius: 999px;
        font-size: 14px;
        font-weight: 600;
        background: linear-gradient(271deg, #329cff 0%, #3d2cff 100%);
        box-shadow: 0 10px 24px rgba(56, 70, 250, 0.2);
        cursor: pointer;
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 14px 28px rgba(56, 70, 250, 0.28);
        }
    }

    /* ---------- 三栏主体（原型 .mega-body + .stack-columns） ---------- */
    .mm-body {
        width: min(1420px, 100%);
        margin: 0 auto;
        /* 原型把 22px 上内边距被横幅的 -22px 外边距抵消，横幅下的实际间距是它的 24px 下外边距 */
        padding: 24px 30px 30px;
        display: grid;
        grid-template-columns: 1.62fr 1fr 0.62fr;
        gap: 34px;
    }

    /* 原型 .mega-body.simple-columns：两栏简易弹层几何（工具&平台、开发者中心共用） */
    .mm-body.is-simple,
    .mm-body.is-doc {
        grid-template-columns: minmax(0, 760px) minmax(280px, 360px);
        justify-content: start;
        gap: 36px;
        padding: 22px 30px 30px;
    }

    .mm-col {
        min-width: 0;
    }

    .mm-col-title {
        margin-bottom: 16px;
        font-size: 18px;
        font-weight: 700;
        line-height: 1.4;
        color: var(--mm-ink);
    }

    /* ---------- 左栏：算子库分组（原型 .operator-groups / .subgroup） ---------- */
    .mm-operator-groups {
        display: grid;
        grid-template-columns: 0.8fr 1.2fr;
        gap: 10px;
    }

    .mm-subgroup {
        margin-bottom: 0;
        padding: 14px;
        background: #fff;
        border: 1px solid var(--mm-line);
        border-radius: 12px;

        /* 「多领域算子库」独占一行（原型 .domain-subgroup） */
        &.is-wide {
            grid-column: 1 / -1;
        }
    }

    .mm-subgroup-title {
        margin-bottom: 9px;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;
        color: #4d5873;
    }

    .mm-link-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 7px;

        &.is-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    /* 原型 .mini-link.described */
    .mm-mini-link {
        min-height: 52px;
        padding: 8px 10px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
        border-radius: 8px;
        color: #343a52;
        font-size: 13px;
        cursor: pointer;
        transition: 0.18s ease;

        &:hover {
            color: var(--mm-brand);
            background: var(--mm-soft-blue);

            .mm-mini-arrow {
                opacity: 1;
                transform: translateX(0);
            }
        }

        b {
            display: block;
            margin-bottom: 4px;
            font-size: 12px;
            font-weight: 700;
            line-height: 1.25;
        }

        small {
            display: block;
            font-size: 10px;
            line-height: 1.35;
            color: var(--mm-muted-2);
        }
    }

    .mm-mini-arrow {
        opacity: 0;
        transform: translateX(-4px);
        transition: 0.18s ease;
    }

    /* ---------- 左栏：基础组件卡（原型 .core-foundations / .foundation-card） ---------- */
    .mm-foundations {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        margin-top: 10px;
    }

    .mm-foundation-card {
        min-height: 74px;
        padding: 12px;
        display: block;
        background: #fafbff;
        border: 1px solid var(--mm-line);
        border-radius: 10px;
        transition: 0.18s ease;

        &:hover {
            transform: translateY(-1px);
            border-color: rgba(56, 70, 250, 0.32);
            background: #f5f7ff;
        }
    }

    .mm-foundation-title {
        margin-bottom: 5px;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;
        color: var(--mm-ink);
    }

    .mm-foundation-link {
        position: relative;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 0;
        color: #4f5870;
        font-size: 11px;
        line-height: 1.5;
        cursor: pointer;

        &:hover {
            color: var(--mm-brand);

            &::after {
                opacity: 1;
                transform: translateX(0);
            }
        }

        &::after {
            content: '→';
            opacity: 0;
            transform: translateX(-3px);
            transition: 0.18s ease;
        }
    }

    /* ---------- 磁贴网格与磁贴（原型 .tile-grid / .menu-tile） ----------
       工具&平台左栏、开发者中心左栏共用 */
    .mm-tile-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 9px;
    }

    .mm-tile {
        position: relative;
        width: 100%;
        min-height: 76px;
        padding: 13px 28px 13px 13px;
        display: block;
        border-radius: 11px;
        color: var(--mm-ink);
        background: #fff;
        cursor: pointer;
        transition: 0.18s ease;

        &:hover {
            background: var(--mm-soft-blue);
            transform: translateY(-1px);
        }

        /* 原型 .menu-tile span：外层包裹与描述同为 11px 次级色 */
        span {
            display: block;
            font-size: 11px;
            line-height: 1.5;
            color: var(--mm-muted);
        }

        /* 原型 .menu-tile b */
        b {
            display: block;
            margin: 1px 0 4px;
            font-size: 13px;
            font-weight: 700;

            &::after {
                content: '→';
                position: absolute;
                right: 13px;
                font-size: 13px;
                line-height: normal;
                color: var(--mm-brand);
                opacity: 0;
                transform: translateX(-4px);
                transition: 0.18s ease;
            }
        }

        &:hover b::after {
            opacity: 1;
            transform: translateX(0);
        }
    }

    /* ---------- 中/右栏：条目列表（原型 .menu-list / .menu-card） ---------- */
    .mm-list {
        display: grid;
        gap: 7px;
    }

    .mm-card {
        position: relative;
        min-height: 58px;
        padding: 10px 30px 10px 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        border-radius: 10px;
        text-align: left;
        cursor: pointer;
        transition: 0.18s ease;

        &:hover {
            background: var(--mm-soft-blue);
        }
    }

    /* 原型 .menu-card b：只声明字号与下边距，行高取浏览器 normal（项目全局继承 20px，需显式还原） */
    .mm-card-name {
        margin-bottom: 4px;
        font-size: 13px;
        font-weight: 700;
        line-height: normal;
        color: var(--mm-ink);

        &::after {
            content: '→';
            position: absolute;
            right: 13px;
            font-size: 13px;
            line-height: normal;
            color: var(--mm-brand);
            opacity: 0;
            transform: translateX(-4px);
            transition: 0.18s ease;
        }
    }

    .mm-card:hover .mm-card-name::after {
        opacity: 1;
        transform: translateX(0);
    }

    .mm-card-desc {
        font-size: 11px;
        line-height: 1.45;
        color: var(--mm-muted);
    }

    /* 原型 .new-badge：改用设计提供的 NEW 图标（icon-svg-new-tag，尺寸取 SVG 自带 32 × 14） */
    .mm-badge {
        margin-left: 5px;
        vertical-align: -2px;
    }
}
</style>
